import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Pill, AlertTriangle, CheckCircle, TrendingUp, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NUTRIENT_LABELS = {
  fiber: { icon: '🌾', color: '#8B5CF6' },
  sodium: { icon: '🧂', color: '#EF4444' },
  sugar: { icon: '🍬', color: '#F59E0B' },
  vitamin_a: { icon: '🥕', color: '#F97316' },
  vitamin_b12: { icon: '🥩', color: '#EC4899' },
  vitamin_c: { icon: '🍊', color: '#F59E0B' },
  vitamin_d: { icon: '☀️', color: '#FBBF24' },
  vitamin_e: { icon: '🥜', color: '#10B981' },
  vitamin_k: { icon: '🥬', color: '#22C55E' },
  calcium: { icon: '🦴', color: '#E5E7EB' },
  iron: { icon: '🔴', color: '#DC2626' },
  potassium: { icon: '🍌', color: '#EAB308' },
  magnesium: { icon: '🌰', color: '#A16207' },
  zinc: { icon: '🔩', color: '#6B7280' },
  omega_3: { icon: '🐟', color: '#3B82F6' },
};

const MicronutrientTracker = ({ token }) => {
  const { t } = useTranslation();
  const [dailyData, setDailyData] = useState(null);
  const [weeklyData, setWeeklyData] = useState(null);
  const [activeTab, setActiveTab] = useState('daily');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [daily, weekly] = await Promise.all([
        axios.get(`${API}/micronutrients/daily`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/micronutrients/weekly`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setDailyData(daily.data);
      setWeeklyData(weekly.data);
    } catch (err) {
      console.error('Error fetching micronutrients:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatName = (key) => {
    return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'good': return 'text-green-600';
      case 'adequate': return 'text-yellow-600';
      case 'high': return 'text-red-600';
      default: return 'text-gray-400';
    }
  };

  const getBarColor = (status) => {
    switch (status) {
      case 'good': return 'bg-green-500';
      case 'adequate': return 'bg-yellow-500';
      case 'high': return 'bg-red-500';
      default: return 'bg-gray-300';
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-500 dark:text-gray-400" data-testid="micro-loading">{t('common.loading')}</div>;
  }

  const nutrients = dailyData?.nutrients || [];
  const deficiencies = weeklyData?.deficiencies || [];
  const excesses = weeklyData?.excesses || [];

  return (
    <div className="space-y-4" data-testid="micronutrient-tracker">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Pill className="text-[#FF6600]" size={24} />
            {t('micro.title')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('micro.subtitle')}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
        {['daily', 'weekly'].map((tab) => (
          <button
            key={tab}
            data-testid={`micro-tab-${tab}`}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === tab
                ? 'bg-white dark:bg-gray-700 text-[#FF6600] shadow-sm'
                : 'text-gray-600 dark:text-gray-300'
            }`}
          >
            {tab === 'daily' ? t('micro.dailyIntake') : t('micro.weeklyAvg')}
          </button>
        ))}
      </div>

      {/* Alerts */}
      {activeTab === 'weekly' && deficiencies.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3" data-testid="micro-deficiency-alert">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={16} className="text-amber-600" />
            <span className="font-semibold text-sm text-amber-800 dark:text-amber-200">{t('micro.lowIntake')}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {deficiencies.map((d) => (
              <span key={d.nutrient} className="text-xs bg-amber-100 dark:bg-amber-800 text-amber-800 dark:text-amber-200 px-2 py-1 rounded-full">
                {NUTRIENT_LABELS[d.nutrient]?.icon} {formatName(d.nutrient)} ({d.percentage}%)
              </span>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'weekly' && excesses.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3" data-testid="micro-excess-alert">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={16} className="text-red-600" />
            <span className="font-semibold text-sm text-red-800 dark:text-red-200">{t('micro.highIntake')}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {excesses.map((e) => (
              <span key={e.nutrient} className="text-xs bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 px-2 py-1 rounded-full">
                {NUTRIENT_LABELS[e.nutrient]?.icon} {formatName(e.nutrient)} ({e.percentage}%)
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Nutrient Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {nutrients.map((nutrient) => {
          const label = NUTRIENT_LABELS[nutrient.key] || {};
          const consumed = activeTab === 'weekly' ? (weeklyData?.averages?.[nutrient.key] || 0) : nutrient.consumed;
          const pct = Math.min((consumed / nutrient.rda) * 100, 200);
          const displayPct = Math.round(pct);
          const status = pct < 50 ? 'low' : pct < 100 ? 'adequate' : pct <= 150 ? 'good' : 'high';

          return (
            <div
              key={nutrient.key}
              data-testid={`micro-${nutrient.key}`}
              className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-100 dark:border-gray-700"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{label.icon}</span>
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-100 dark:text-gray-200">{formatName(nutrient.key)}</span>
                </div>
                <span className={`text-xs font-semibold ${getStatusColor(status)}`}>
                  {displayPct}%
                </span>
              </div>
              <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mb-1">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${getBarColor(status)}`}
                  style={{ width: `${Math.min(pct, 100)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>{Math.round(consumed * 10) / 10}{nutrient.unit}</span>
                <span>{nutrient.rda}{nutrient.unit} RDA</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Info */}
      {dailyData?.meals_counted === 0 && (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400" data-testid="micro-no-data">
          <Info size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">{t('micro.noData')}</p>
        </div>
      )}
    </div>
  );
};

export default MicronutrientTracker;
